import React, { useState, useEffect, useRef, useMemo } from 'react';
import { User } from '../../services/authService';
import {
  getUserPlants,
  getUserConfig,
  GardenArea,
  GardenPlant
} from '../../services/gardenService';
import PageHeader from '../common/PageHeader';
import GardenAreaComponent from './GardenArea';
import PlantItem from './PlantItem';
import PlantNotePopover from './PlantNotePopover';
import BirthDateSetup from './BirthDateSetup';
import PlantGenerationDialog from './PlantGenerationDialog';
import GardenInfoDialog from './GardenInfoDialog';
import JumpToYearDialog from './JumpToYearDialog';
import { calculateAllPlantPositions, calculateRequiredHeight, calculateEmptyCellPositions, EmptyCell } from '../../utils/gardenHelpers';
import './Garden.css';

interface GardenProps {
  user: User | null;
  onToggleSidebar: () => void;
  onSignOut: () => void;
  onClose: () => void;
}

const Garden: React.FC<GardenProps> = ({ user, onToggleSidebar }) => {
  const [areas, setAreas] = useState<GardenArea[]>([]);
  const [plants, setPlants] = useState<GardenPlant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [selectedPlant, setSelectedPlant] = useState<GardenPlant | null>(null);
  const [showBirthDateSetup, setShowBirthDateSetup] = useState(false);
  const [showGenerationDialog, setShowGenerationDialog] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [showJumpToYearDialog, setShowJumpToYearDialog] = useState(false);
  const [birthDate, setBirthDate] = useState<string | null>(null);
  const [focusedYear, setFocusedYear] = useState<number | null>(null);
  const [screenSize, setScreenSize] = useState({ width: 0, height: 0 });
  const canvasRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasScrolledToCurrentYear = useRef(false);

  useEffect(() => {
    const updateScreenSize = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;
        
        // Only update if we have valid dimensions
        if (width > 0 && height > 0) {
          setScreenSize({ width, height });
        }
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const rafId = requestAnimationFrame(() => {
      updateScreenSize();
    });

    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateScreenSize, 200);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const generateYearlyAreas = (birthDateStr: string, userId: number, screenWidth: number, screenHeight: number) => {
    if (screenWidth === 0 || screenHeight === 0) {
      return { generatedAreas: [], totalPages: 0 };
    }

    const birthDate = new Date(birthDateStr);
    const today = new Date();
    const birthYear = birthDate.getFullYear();
    const currentYear = today.getFullYear();

    const generatedAreas: GardenArea[] = [];

    for (let year = birthYear; year <= currentYear; year++) {
      const lifeYear = year - birthYear;
      const pageIndex = year - birthYear;

      const area: GardenArea = {
        id: year,
        user_id: userId,
        name: `${year} (Age ${lifeYear})`,
        position_x: pageIndex * screenWidth,
        position_y: 0,
        width: screenWidth,
        height: screenHeight,
        color: year === currentYear ? '#cfe8cf' : '#f0f0f0',
        year_number: year,
        life_year_label: `Age ${lifeYear}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      generatedAreas.push(area);
    }

    const totalPages = currentYear - birthYear + 1;
    return { generatedAreas, totalPages };
  };

  useEffect(() => {
    if (user?.id) {
      checkBirthDateAndLoad();
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMenu && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const checkBirthDateAndLoad = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      // Check if birth date is set
      const configResponse = await getUserConfig(user.id);
      if (configResponse.success) {
        if (!configResponse.birth_date) {
          setShowBirthDateSetup(true);
          setLoading(false);
          return;
        }
        setBirthDate(configResponse.birth_date);
      }

      // Load garden data
      await loadData();
    } catch (err) {
      console.error('Error checking birth date:', err);
      setError('Failed to load garden configuration');
      setLoading(false);
    }
  };

  const loadData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const plantsResponse = await getUserPlants(user.id);

      if (plantsResponse.success && plantsResponse.plants) {
        setPlants(plantsResponse.plants);
      }
    } catch (err) {
      console.error('Error loading garden data:', err);
      setError('Failed to load garden data');
    } finally {
      setLoading(false);
    }
  };

  const handleBirthDateComplete = async () => {
    setShowBirthDateSetup(false);
    hasScrolledToCurrentYear.current = false; // Reset scroll flag for new birth date
    await checkBirthDateAndLoad();
  };

  const handleGenerationComplete = async () => {
    setShowGenerationDialog(false);
    await loadData();
  };

  const handlePlantClick = (e: React.MouseEvent, plant: GardenPlant) => {
    setSelectedPlant(plant);
  };

  const handleClosePopover = () => {
    setSelectedPlant(null);
  };

  const getPlantsForArea = (areaYear: number): GardenPlant[] => {
    return plants.filter(plant => plant.plant_date && new Date(plant.plant_date).getFullYear() === areaYear);
  };

  useEffect(() => {
    if (birthDate && user?.id && screenSize.width > 0 && screenSize.height > 0) {
      const { generatedAreas } = generateYearlyAreas(birthDate, user.id, screenSize.width, screenSize.height);
      
      const updatedAreas = generatedAreas.map(area => {
        if (!area.year_number) return area;
        
        const requiredHeight = calculateRequiredHeight(area.width, area.year_number);
        
        return {
          ...area,
          height: Math.max(screenSize.height, requiredHeight)
        };
      });

      setAreas(updatedAreas);
    }
  }, [birthDate, user?.id, screenSize, plants]);

  const scrollToCurrentYear = () => {
    if (
      areas.length > 0 && 
      birthDate && 
      canvasRef.current && 
      screenSize.width > 0
    ) {
      const birthYear = new Date(birthDate).getFullYear();
      const currentYear = new Date().getFullYear();
      scrollToYear(currentYear);
    }
  };

  const getLastVisitedYear = (userId: number): number | null => {
    try {
      const stored = localStorage.getItem(`garden_last_year_${userId}`);
      if (stored) {
        const year = parseInt(stored, 10);
        return isNaN(year) ? null : year;
      }
    } catch (err) {
      console.error('Error reading last visited year from localStorage:', err);
    }
    return null;
  };

  const saveLastVisitedYear = (userId: number, year: number) => {
    try {
      localStorage.setItem(`garden_last_year_${userId}`, year.toString());
    } catch (err) {
      console.error('Error saving last visited year to localStorage:', err);
    }
  };

  const scrollToYear = (year: number) => {
    if (
      areas.length > 0 && 
      birthDate && 
      canvasRef.current && 
      screenSize.width > 0
    ) {
      const birthYear = new Date(birthDate).getFullYear();
      const currentYear = new Date().getFullYear();
      
      // Validate year is within range
      if (year < birthYear || year > currentYear) {
        return;
      }
      
      // Save to localStorage immediately
      if (user?.id) {
        saveLastVisitedYear(user.id, year);
      }
      
      const yearIndex = year - birthYear;
      const scrollPosition = yearIndex * screenSize.width;
      
      // Use multiple animation frames and setTimeout to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (canvasRef.current) {
              canvasRef.current.scrollTo({
                left: scrollPosition,
                behavior: 'smooth'
              });
            }
          }, 100);
        });
      });
    }
  };

  // Track focused year based on scroll position and save to localStorage
  useEffect(() => {
    if (!canvasRef.current || !birthDate || screenSize.width === 0 || areas.length === 0 || !user?.id) {
      return;
    }

    const updateFocusedYear = () => {
      if (!canvasRef.current || !birthDate || !user?.id) return;
      
      const scrollLeft = canvasRef.current.scrollLeft;
      const birthYear = new Date(birthDate).getFullYear();
      const yearIndex = Math.round(scrollLeft / screenSize.width);
      const year = birthYear + yearIndex;
      
      // Validate year is within range
      const currentYear = new Date().getFullYear();
      if (year >= birthYear && year <= currentYear) {
        setFocusedYear(year);
        // Save to localStorage
        saveLastVisitedYear(user.id, year);
      }
    };

    const canvas = canvasRef.current;
    canvas.addEventListener('scroll', updateFocusedYear);
    updateFocusedYear(); // Initial update

    return () => {
      canvas.removeEventListener('scroll', updateFocusedYear);
    };
  }, [birthDate, screenSize.width, areas.length, user?.id]);

  // Scroll to last visited year or current year on initial load
  useEffect(() => {
    if (
      !hasScrolledToCurrentYear.current &&
      areas.length > 0 && 
      birthDate && 
      canvasRef.current && 
      screenSize.width > 0 &&
      !loading &&
      user?.id
    ) {
      const birthYear = new Date(birthDate).getFullYear();
      const currentYear = new Date().getFullYear();
      
      // Try to get last visited year from localStorage
      const lastVisitedYear = getLastVisitedYear(user.id);
      
      // Validate last visited year is still within range
      if (lastVisitedYear && lastVisitedYear >= birthYear && lastVisitedYear <= currentYear) {
        scrollToYear(lastVisitedYear);
      } else {
        // Fallback to current year
        scrollToCurrentYear();
      }
      
      // Mark that we've attempted to scroll
      hasScrolledToCurrentYear.current = true;
    }
  }, [areas.length, birthDate, screenSize.width, loading, user?.id]);

  const plantPositions = useMemo(() => {
    return calculateAllPlantPositions(areas, plants);
  }, [areas, plants]);

  const emptyCellPositions = useMemo(() => {
    return calculateEmptyCellPositions(areas, plants);
  }, [areas, plants]);

  const totalCanvasWidth = areas.length * screenSize.width;

  const maxAreaHeight = useMemo(() => {
    if (areas.length === 0) {
      return screenSize.height;
    }
    const heights = areas.map(a => a.height).filter(h => typeof h === 'number');
    return heights.length > 0 ? Math.max(...heights) : screenSize.height;
  }, [areas, screenSize.height]);

  if (loading || screenSize.width === 0 || screenSize.height === 0) {
    return (
      <div className="garden-container" ref={containerRef}>
        <div className="garden-loading">Loading garden...</div>
      </div>
    );
  }

  if (showBirthDateSetup && user?.id) {
    return (
      <div className="garden-container">
        <BirthDateSetup
          userId={user.id}
          onComplete={handleBirthDateComplete}
        />
      </div>
    );
  }

  return (
    <div className="garden-container" ref={containerRef}>
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">/garden</h1>}
        centerClassName="garden-title"
        rightActions={
          <div className="garden-menu-container" ref={menuRef}>
            <button
              className="garden-menu-btn"
              onClick={() => setShowMenu(!showMenu)}
              title="Menu"
            >
              ⋮
            </button>
            {showMenu && (
              <div className="garden-menu-dropdown">
                <button
                  className="garden-menu-item"
                  onClick={() => {
                    scrollToCurrentYear();
                    setShowMenu(false);
                  }}
                >
                  Scroll to Current Year
                </button>
                <button
                  className="garden-menu-item"
                  onClick={() => {
                    setShowJumpToYearDialog(true);
                    setShowMenu(false);
                  }}
                >
                  Jump to Year
                </button>
                <button
                  className="garden-menu-item"
                  onClick={() => {
                    setShowGenerationDialog(true);
                    setShowMenu(false);
                  }}
                >
                  Generate Plants
                </button>
                <button
                  className="garden-menu-item"
                  onClick={() => {
                    setShowInfoDialog(true);
                    setShowMenu(false);
                  }}
                >
                  Info
                </button>
              </div>
            )}
          </div>
        }
      />

      {error && (
        <div className="garden-error">{error}</div>
      )}

      <div className="garden-canvas-container" ref={canvasRef}>
        <div
          className="garden-canvas"
          style={{ 
            width: totalCanvasWidth, 
            height: maxAreaHeight,
            position: 'relative'
          }}
        >
          {areas.map(area => (
            <GardenAreaComponent
              key={area.id}
              area={area}
              plantCount={getPlantsForArea(area.id).length}
            />
          ))}
          {areas.map(area => {
            const emptyCells = emptyCellPositions.get(area.id) || [];
            return emptyCells.map((emptyCell: EmptyCell) => (
              <div
                key={`empty-${area.id}-${emptyCell.dayOfYear}`}
                className="garden-empty-cell"
                style={{
                  left: emptyCell.position.x,
                  top: emptyCell.position.y,
                }}
              />
            ));
          })}
          {plants.map(plant => {
            const position = plantPositions.get(plant.id) || { x: plant.position_x || 0, y: plant.position_y || 0 };
            return (
              <PlantItem
                key={plant.id}
                plant={plant}
                position={position}
                onClick={handlePlantClick}
                isReadOnly={true}
              />
            );
          })}
          {selectedPlant && (
            <PlantNotePopover
              plant={selectedPlant}
              onClose={handleClosePopover}
              isReadOnly={true}
            />
          )}
        </div>
      </div>

      {showGenerationDialog && user?.id && (
        <PlantGenerationDialog
          userId={user.id}
          focusedYear={focusedYear}
          onClose={() => setShowGenerationDialog(false)}
          onComplete={handleGenerationComplete}
        />
      )}

      {showInfoDialog && (
        <GardenInfoDialog
          onClose={() => setShowInfoDialog(false)}
        />
      )}

      {showJumpToYearDialog && birthDate && (
        <JumpToYearDialog
          birthYear={new Date(birthDate).getFullYear()}
          currentYear={new Date().getFullYear()}
          onClose={() => setShowJumpToYearDialog(false)}
          onJump={scrollToYear}
        />
      )}
    </div>
  );
};

export default Garden;
